import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function About() {
  // SEO Configuration
  useSEO({
    title: 'About Rooted & Rising - Our Mission, Values & Story',
    description: 'Learn about Rooted & Rising\'s mission to help families build micro-habits that create lasting transformation. Discover our values, philosophy, and commitment to intentional family living.',
    keywords: 'about rooted and rising, family mission, intentional living philosophy, micro-habits mission, family values, faith-based family approach',
    canonical: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Rooted & Rising',
      description: 'A family operating system for intentional living',
      url: `${import.meta.env.VITE_SITE_URL || ''}/about`,
      mainEntity: {
        '@type': 'Organization',
        name: 'Rooted & Rising',
        description: 'To help families build micro-habits that create lasting transformation—rooted in what matters most, rising through small daily actions.',
        url: import.meta.env.VITE_SITE_URL || ''
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-amber-50 to-rose-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About Rooted &amp; Rising
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            A family operating system for intentional living
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              To help families build micro-habits that create lasting transformation—rooted in what matters most, rising through small daily actions.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              In a world of constant overwhelm, families are searching for a better way. Not another productivity hack or parenting trend, but a sustainable framework for building a life that truly matters.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Rooted &amp; Rising was born from a simple truth: transformation doesn't require massive overhauls. It happens through small, intentional actions repeated consistently over time. When families build these micro-habits together, they create compound growth that touches every area of life.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              We believe in the power of starting small, stacking habits strategically, and growing together. We believe that faith, family, friends, fitness, finances, food, and fun aren't separate categories—they're interconnected pillars that support a whole, balanced life.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-500 rounded-full mb-6">
                <i className="ri-seedling-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Start Small</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe in the power of micro-habits. Small actions, done consistently, create extraordinary results without the overwhelm.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-500 rounded-full mb-6">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Grow Together</h3>
              <p className="text-gray-600 leading-relaxed">
                Shared habits create connection. When families build rhythms together, they strengthen bonds while growing individually.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-500 rounded-full mb-6">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Faith-Forward</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe faith is the foundation that grounds families in purpose, gratitude, and meaning beyond themselves.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-violet-500 rounded-full mb-6">
                <i className="ri-compass-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Holistic Living</h3>
              <p className="text-gray-600 leading-relaxed">
                True transformation touches every area of life. The seven pillars work together to create balance and wholeness.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-cyan-500 rounded-full mb-6">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Long-Term Thinking</h3>
              <p className="text-gray-600 leading-relaxed">
                We're not chasing quick fixes. We're building habits that compound over a lifetime, creating lasting legacy.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-500 rounded-full mb-6">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Practical Wisdom</h3>
              <p className="text-gray-600 leading-relaxed">
                Every idea we share is actionable, realistic, and designed for real families with busy lives and limited time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 lg:h-full rounded-2xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/4f127bf1af809822282867761a787ea2.jpg"
                alt="Family growing together"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Rooted &amp; Rising?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The name captures our philosophy: families need to be <strong>rooted</strong> in what matters most—faith, values, and connection—while continually <strong>rising</strong> through small, intentional actions that create growth.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Like a tree with deep roots and branches reaching skyward, families thrive when they're grounded in purpose while stretching toward their potential. Micro-habits are the daily nourishment that makes both possible.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We're here to provide the framework, guidance, and tools to help your family build this life—one small habit at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-amber-500 to-rose-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Us on the Journey
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start building micro-habits that will transform your family for generations to come
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/how-it-works" 
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
            <Link 
              to="/blog" 
              className="px-8 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Read the Blog
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}